import { FileUp, PenLine, Trash2, X } from 'lucide-react';
import { useRef, useState } from 'react';
import type { CheckInEvidence } from '../../../../../packages/contracts/src/index.js';
import { api } from '../../shared/api';
import { Button } from '../ui/button';
import { Input, Label } from '../ui/field';

export interface CheckInTarget {
  readonly id: string;
  readonly label: string;
  readonly kind: 'daycare' | 'stay';
}

export function CheckInEvidenceDialog({
  target,
  onClose,
  onSubmit,
}: {
  readonly target: CheckInTarget | null;
  readonly onClose: () => void;
  readonly onSubmit: (evidence: CheckInEvidence) => Promise<void> | void;
}) {
  const signatureRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const hasSignatureRef = useRef(false);
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<'signature' | 'file'>('signature');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  if (!target) return null;

  const submit = async () => {
    setError('');
    setIsSaving(true);
    try {
      const uploadFile = mode === 'file' ? file : await signatureToFile(signatureRef.current, hasSignatureRef.current);
      if (!uploadFile) throw new Error(mode === 'file' ? 'Selecciona un archivo.' : 'Firma antes de continuar.');
      if (uploadFile.size > 10 * 1024 * 1024) throw new Error('El archivo debe pesar 10 MB o menos.');
      const upload = await api.files.createUpload({
        fileName: uploadFile.name,
        contentType: uploadFile.type || 'application/octet-stream',
        sizeBytes: uploadFile.size,
        purpose: 'document',
        ownerId: target.id,
      });
      const response = await fetch(upload.uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': uploadFile.type || 'application/octet-stream', 'if-none-match': '*' },
        body: uploadFile,
      });
      if (!response.ok) throw new Error('No se pudo subir el archivo.');
      await onSubmit({
        fileId: upload.fileId,
        objectKey: upload.objectKey,
        fileName: uploadFile.name,
        contentType: uploadFile.type || 'application/octet-stream',
        kind: mode === 'file' ? 'document' : 'signature',
        capturedAt: new Date().toISOString(),
      });
      onClose();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'No se pudo registrar la entrada.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/35 p-4">
      <div className="w-full max-w-xl rounded-md border border-[#d6ded0] bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-[#eef3ea] p-4">
          <div>
            <h2 className="text-base font-semibold text-slate-950">Evidencia de entrada</h2>
            <p className="mt-1 text-sm text-[#687461]">{target.label}</p>
          </div>
          <button type="button" className="rounded-md p-1 text-[#687461] hover:bg-[#eef3ea]" aria-label="Cerrar" onClick={onClose}>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="grid gap-4 p-4">
          <div className="inline-flex w-fit overflow-hidden rounded-md border border-[#d6ded0] bg-white">
            <button type="button" className={`inline-flex h-9 items-center gap-2 px-3 text-sm font-medium ${mode === 'signature' ? 'bg-[#334033] text-white' : 'text-[#334033] hover:bg-[#eef3ea]'}`} onClick={() => setMode('signature')}>
              <PenLine className="h-4 w-4" aria-hidden="true" />
              Firma digital
            </button>
            <button type="button" className={`inline-flex h-9 items-center gap-2 px-3 text-sm font-medium ${mode === 'file' ? 'bg-[#334033] text-white' : 'text-[#334033] hover:bg-[#eef3ea]'}`} onClick={() => setMode('file')}>
              <FileUp className="h-4 w-4" aria-hidden="true" />
              Archivo
            </button>
          </div>

          {mode === 'signature' ? (
            <div className="grid gap-2">
              <div className="overflow-hidden rounded-md border border-[#d6ded0] bg-white">
                <canvas
                  ref={signatureRef}
                  className="h-44 w-full touch-none bg-white"
                  aria-label="Firma digital"
                  onPointerDown={(event) => startSignature(event, drawingRef, hasSignatureRef)}
                  onPointerMove={(event) => drawSignature(event, drawingRef, hasSignatureRef)}
                  onPointerUp={() => { drawingRef.current = false; }}
                  onPointerLeave={() => { drawingRef.current = false; }}
                />
              </div>
              <Button type="button" variant="secondary" className="w-fit" onClick={() => clearSignature(signatureRef.current, hasSignatureRef)}>
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                Limpiar firma
              </Button>
            </div>
          ) : (
            <div className="grid gap-1.5">
              <Label htmlFor="checkInEvidenceFile">Documento firmado</Label>
              <Input id="checkInEvidenceFile" type="file" accept="image/*,.pdf" onChange={(event) => setFile(event.target.files?.[0] ?? null)} />
            </div>
          )}

          {error ? <p className="text-sm text-rose-600">{error}</p> : null}

          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>Cancelar</Button>
            <Button type="button" onClick={submit} disabled={isSaving}>{isSaving ? 'Guardando...' : 'Registrar entrada'}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function startSignature(event: React.PointerEvent<HTMLCanvasElement>, drawingRef: React.MutableRefObject<boolean>, hasSignatureRef: React.MutableRefObject<boolean>) {
  const canvas = event.currentTarget;
  setupCanvas(canvas);
  const context = canvas.getContext('2d');
  if (!context) return;
  drawingRef.current = true;
  hasSignatureRef.current = true;
  const point = canvasPoint(canvas, event);
  context.beginPath();
  context.moveTo(point.x, point.y);
  canvas.setPointerCapture(event.pointerId);
}

function drawSignature(event: React.PointerEvent<HTMLCanvasElement>, drawingRef: React.MutableRefObject<boolean>, hasSignatureRef: React.MutableRefObject<boolean>) {
  if (!drawingRef.current) return;
  const canvas = event.currentTarget;
  setupCanvas(canvas);
  const context = canvas.getContext('2d');
  if (!context) return;
  hasSignatureRef.current = true;
  const point = canvasPoint(canvas, event);
  context.lineTo(point.x, point.y);
  context.strokeStyle = '#223126';
  context.lineWidth = 2.5;
  context.lineCap = 'round';
  context.lineJoin = 'round';
  context.stroke();
}

function clearSignature(canvas: HTMLCanvasElement | null, hasSignatureRef: React.MutableRefObject<boolean>) {
  if (!canvas) return;
  setupCanvas(canvas);
  canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
  hasSignatureRef.current = false;
}

async function signatureToFile(canvas: HTMLCanvasElement | null, hasSignature: boolean) {
  if (!canvas || !hasSignature) return null;
  setupCanvas(canvas);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) return null;
  return new File([blob], `firma-entrada-${Date.now()}.png`, { type: 'image/png' });
}

function setupCanvas(canvas: HTMLCanvasElement) {
  const rect = canvas.getBoundingClientRect();
  const scale = window.devicePixelRatio || 1;
  const width = Math.max(1, Math.floor(rect.width * scale));
  const height = Math.max(1, Math.floor(rect.height * scale));
  if (canvas.width === width && canvas.height === height) return;
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return;
  context.scale(scale, scale);
  context.strokeStyle = '#223126';
  context.lineWidth = 2.5;
  context.lineCap = 'round';
  context.lineJoin = 'round';
}

function canvasPoint(canvas: HTMLCanvasElement, event: React.PointerEvent<HTMLCanvasElement>) {
  const rect = canvas.getBoundingClientRect();
  return { x: event.clientX - rect.left, y: event.clientY - rect.top };
}
